import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  QueryConstraint,
  QueryDocumentSnapshot,
  QuerySnapshot,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getAuth } from "firebase/auth";
import { db, app } from "./init";
import { convertTimestampToDate } from "./firestore";
import { User } from "@/types/user";
import { Receipt } from "@/types/receipt";
import { FailedPurchase } from "@/types/failedPurchase";
import { WatchHistory } from "@/types/watchHistory";
import { Favorite } from "@/types/favorite";

/**
 * 搜尋使用者（使用 Cloud Function）
 * 支援 email、displayName、uid 的搜尋
 * 
 * 優先使用 Cloud Function 進行搜尋，如果失敗則回退到客戶端搜尋
 * 
 * @param searchQuery - 搜尋關鍵字（email、姓名或使用者 ID）
 * @param options - 搜尋選項（僅在回退到客戶端搜尋時使用）
 * @param options.maxUsersToLoad - 最大載入用戶數量（預設 1000）
 * @param options.batchSize - 每批載入數量（預設 100）
 * @param options.stopOnFirstMatch - 找到第一個匹配後是否停止（預設 false）
 */
export async function searchUsers(
  searchQuery: string,
  options?: {
    maxUsersToLoad?: number;
    batchSize?: number;
    stopOnFirstMatch?: boolean;
  }
): Promise<User[]> {
  // 優先使用 Cloud Function 進行搜尋
  try {
    console.log("[searchUsers] 嘗試使用 Cloud Function 搜尋:", searchQuery);
    // 使用與其他 Functions 相同的初始化方式（指定區域）
    const { app } = await import("./init");
    const functions = getFunctions(app, "asia-east1");
    const searchUsersFunction = httpsCallable(functions, "searchUsers");
    const result = await searchUsersFunction({ searchQuery });
    
    const users = (result.data as any[]) || [];
    console.log("[searchUsers] Cloud Function 搜尋成功，找到", users.length, "個結果");
    
    // 轉換資料格式以符合 User 類型
    return users.map((user: any) => {
      const converted = convertTimestampToDate(user);
      return {
        id: user.id || user.uid,
        ...converted,
        createdAt: converted.created_at || converted.createdAt,
        updatedAt: converted.updated_at || converted.updatedAt,
        created_at: converted.created_at || converted.createdAt,
        updated_at: converted.updated_at || converted.updatedAt,
      } as User;
    });
  } catch (functionError: any) {
    console.warn("[searchUsers] Cloud Function 搜尋失敗，回退到客戶端搜尋:", functionError);
    console.warn("[searchUsers] 錯誤詳情:", {
      code: functionError.code,
      message: functionError.message,
    });
    
    // 回退到原有的客戶端搜尋邏輯
    return searchUsersClientSide(searchQuery, options);
  }
}

/**
 * 客戶端搜尋使用者（備份方案）
 * 當 Cloud Function 不可用時使用
 */
async function searchUsersClientSide(
  searchQuery: string,
  options?: {
    maxUsersToLoad?: number;
    batchSize?: number;
    stopOnFirstMatch?: boolean;
  }
): Promise<User[]> {
  const maxUsersToLoad = options?.maxUsersToLoad ?? 1000;
  const batchSize = options?.batchSize ?? 100;
  const stopOnFirstMatch = options?.stopOnFirstMatch ?? false;
  console.log("[searchUsers] 開始搜尋，查詢字串:", searchQuery);
  try {
    if (!searchQuery || searchQuery.trim().length === 0) {
      console.log("[searchUsers] 查詢字串為空，返回空陣列");
      return [];
    }

    // 檢查當前用戶的認證狀態
    const { auth } = await import("./init");
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.error("[searchUsers] 用戶未登入");
      throw new Error("用戶未登入，無法搜尋使用者");
    }
    
    // 檢查權限（從 token 讀取）
    try {
      const idTokenResult = await currentUser.getIdTokenResult();
      const isStaff = idTokenResult.claims.admin === true || idTokenResult.claims.staff === true;
      console.log("[searchUsers] 用戶權限檢查:", {
        uid: currentUser.uid,
        email: currentUser.email,
        isAdmin: idTokenResult.claims.admin === true,
        isStaff: idTokenResult.claims.staff === true,
        hasPermission: isStaff
      });
      
      if (!isStaff) {
        console.warn("[searchUsers] 用戶沒有 staff 權限，搜尋可能失敗");
      }
    } catch (authError) {
      console.warn("[searchUsers] 無法檢查用戶權限:", authError);
    }

    const originalQuery = searchQuery.trim();
    const queryStr = originalQuery.toLowerCase();
    const usersCollection = collection(db, "users");
    const results: User[] = [];
    console.log("[searchUsers] 處理後的查詢字串:", queryStr);
    
    // 測試查詢：檢查 Firestore 中是否有任何 users 文檔
    console.log("[searchUsers] 開始測試查詢...");
    try {
      // 測試 1: 嘗試讀取當前用戶的文檔
      console.log("[searchUsers] 測試 1: 讀取當前用戶文檔，UID:", currentUser.uid);
      try {
        const currentUserDoc = await getDoc(doc(db, "users", currentUser.uid));
        if (currentUserDoc.exists()) {
          const currentUserData = currentUserDoc.data();
          console.log("[searchUsers] ✅ 當前用戶文檔存在:", {
            id: currentUserDoc.id,
            email: currentUserData.email,
            hasEmail: !!currentUserData.email,
            emailType: typeof currentUserData.email,
            emailValue: JSON.stringify(currentUserData.email),
            allFields: Object.keys(currentUserData)
          });
        } else {
          console.warn("[searchUsers] ⚠️ 當前用戶文檔不存在於 Firestore");
        }
      } catch (currentUserError) {
        console.error("[searchUsers] ❌ 讀取當前用戶文檔失敗:", currentUserError);
      }
      
      // 測試 2: 嘗試查詢所有 users
      const testQuery = query(usersCollection, limit(5));
      const testSnapshot = await getDocs(testQuery);
      console.log("[searchUsers] 測試 2: 查詢所有 users，找到", testSnapshot.size, "個文檔");
      if (testSnapshot.size > 0) {
        testSnapshot.docs.forEach((doc, index) => {
          const sampleData = doc.data();
          const allFields = Object.keys(sampleData);
          console.log(`[searchUsers] 範例文檔 #${index + 1}:`, {
            id: doc.id,
            hasEmail: !!sampleData.email,
            email: sampleData.email,
            emailType: typeof sampleData.email,
            emailValue: JSON.stringify(sampleData.email),
            hasCreatedAt: !!sampleData.created_at,
            hasCreatedAtCamel: !!sampleData.createdAt,
            allFields: allFields,
            fieldValues: allFields.reduce((acc, key) => {
              const value = sampleData[key];
              acc[key] = typeof value === 'string' ? value.substring(0, 50) : value;
              return acc;
            }, {} as Record<string, any>)
          });
        });
        console.warn("[searchUsers] ⚠️ 重要發現：所有用戶文檔的 email 欄位都是 undefined！");
        console.warn("[searchUsers] 這表示 Firestore 中的 users 文檔沒有保存 email 欄位。");
        console.warn("[searchUsers] 建議：需要從 Firebase Auth 同步 email 到 Firestore，或檢查用戶創建邏輯。");
      } else {
        console.warn("[searchUsers] ⚠️ 警告：Firestore 中沒有找到任何 users 文檔！");
      }
    } catch (testError) {
      console.error("[searchUsers] ❌ Firestore 測試查詢失敗:", testError);
      console.error("[searchUsers] 錯誤詳情:", {
        message: testError instanceof Error ? testError.message : String(testError),
        code: (testError as any)?.code,
        stack: testError instanceof Error ? testError.stack : undefined
      });
    }

    // 1. 嘗試精確匹配 email（如果查詢字串看起來像 email）
    // 注意：由於 Firestore 中的 users 文檔可能沒有 email 欄位，這個查詢可能總是返回 0
    if (queryStr.includes("@")) {
      console.warn("[searchUsers] ⚠️ 嘗試通過 email 搜尋，但 Firestore 中的 users 文檔可能沒有 email 欄位");
      try {
        // 先嘗試原始值完全匹配（大小寫敏感）
        try {
          const exactEmailQueryOriginal = query(
            usersCollection,
            where("email", "==", originalQuery),
            limit(10)
          );
          const exactSnapshotOriginal = await getDocs(exactEmailQueryOriginal);
          console.log("[searchUsers] 原始值完全匹配找到", exactSnapshotOriginal.size, "個結果");
          console.log("[searchUsers] 查詢條件: email == ", originalQuery);
          if (exactSnapshotOriginal.size > 0) {
            console.log("[searchUsers] ✅ 匹配的 email:", Array.from(exactSnapshotOriginal.docs).map(d => ({ id: d.id, email: d.data().email })));
          } else {
            console.warn("[searchUsers] ⚠️ 沒有找到匹配的 email，查詢條件:", originalQuery);
            console.warn("[searchUsers] 💡 提示：Firestore 中的 users 文檔可能沒有 email 欄位，請使用 UID 搜尋或檢查數據同步邏輯");
          }
          exactSnapshotOriginal.forEach((doc) => {
            const data = doc.data();
            const converted = convertTimestampToDate(data);
            const user = {
              id: doc.id,
              ...converted,
              createdAt: converted.created_at || converted.createdAt,
              updatedAt: converted.updated_at || converted.updatedAt,
              created_at: converted.created_at || converted.createdAt,
              updated_at: converted.updated_at || converted.updatedAt,
            } as User;
            if (!results.find((u) => u.id === user.id)) {
              results.push(user);
            }
          });
        } catch (err) {
          console.debug("Original case email search failed:", err);
        }

        // 再嘗試小寫值完全匹配（如果原始值沒找到結果）
        if (results.length === 0 && originalQuery.toLowerCase() !== originalQuery) {
          try {
            const exactEmailQuery = query(
              usersCollection,
              where("email", "==", queryStr),
              limit(10)
            );
            const exactSnapshot = await getDocs(exactEmailQuery);
            console.log("[searchUsers] 小寫值完全匹配找到", exactSnapshot.size, "個結果");
            if (exactSnapshot.size > 0) {
              console.log("[searchUsers] 匹配的 email:", Array.from(exactSnapshot.docs).map(d => d.data().email));
            }
            exactSnapshot.forEach((doc) => {
              const data = doc.data();
              const converted = convertTimestampToDate(data);
              const user = {
                id: doc.id,
                ...converted,
                createdAt: converted.created_at || converted.createdAt,
                updatedAt: converted.updated_at || converted.updatedAt,
                created_at: converted.created_at || converted.createdAt,
                updated_at: converted.updated_at || converted.updatedAt,
              } as User;
              if (!results.find((u) => u.id === user.id)) {
                results.push(user);
              }
            });
          } catch (err) {
            console.debug("Lowercase email search failed:", err);
          }
        }

        // 如果沒有完全匹配，嘗試前綴匹配（需要索引）
        if (results.length === 0) {
          try {
            // 先嘗試原始值前綴匹配
            const emailQueryOriginal = query(
              usersCollection,
              where("email", ">=", originalQuery),
              where("email", "<=", originalQuery + "\uf8ff"),
              limit(20)
            );
            const emailSnapshotOriginal = await getDocs(emailQueryOriginal);
            emailSnapshotOriginal.forEach((doc) => {
              const data = doc.data();
              const converted = convertTimestampToDate(data);
              const user = {
                id: doc.id,
                ...converted,
                createdAt: converted.created_at || converted.createdAt,
                updatedAt: converted.updated_at || converted.updatedAt,
                created_at: converted.created_at || converted.createdAt,
                updated_at: converted.updated_at || converted.updatedAt,
              } as User;
              if (!results.find((u) => u.id === user.id)) {
                results.push(user);
              }
            });
          } catch (err) {
            // 如果原始值前綴匹配失敗，嘗試小寫值
            if (originalQuery.toLowerCase() !== originalQuery) {
              try {
                const emailQuery = query(
                  usersCollection,
                  where("email", ">=", queryStr),
                  where("email", "<=", queryStr + "\uf8ff"),
                  limit(20)
                );
                const emailSnapshot = await getDocs(emailQuery);
                emailSnapshot.forEach((doc) => {
                  const data = doc.data();
                  const converted = convertTimestampToDate(data);
                  const user = {
                    id: doc.id,
                    ...converted,
                    createdAt: converted.created_at || converted.createdAt,
                    updatedAt: converted.updated_at || converted.updatedAt,
                    created_at: converted.created_at || converted.createdAt,
                    updated_at: converted.updated_at || converted.updatedAt,
                  } as User;
                  if (!results.find((u) => u.id === user.id)) {
                    results.push(user);
                  }
                });
              } catch (prefixErr) {
                console.warn("Email prefix search failed (index may be missing):", prefixErr);
              }
            } else {
              console.warn("Email prefix search failed (index may be missing):", err);
            }
          }
        }
      } catch (error) {
        console.error("Email search error:", error);
      }
    }

    // 2. 嘗試精確匹配 uid (id)
    // 注意：UID 是大小寫敏感的，所以使用原始查詢字串
    if (originalQuery.length >= 10) {
      // uid 通常較長，只有當搜尋字串足夠長時才嘗試
      console.log("[searchUsers] 嘗試通過 UID 搜尋:", originalQuery);
      try {
        const userDoc = await getDoc(doc(db, "users", originalQuery));
        if (userDoc.exists()) {
          console.log("[searchUsers] ✅ 通過 UID 找到用戶:", originalQuery);
          const data = userDoc.data();
          const converted = convertTimestampToDate(data);
          const user = {
            id: userDoc.id,
            ...converted,
            createdAt: converted.created_at || converted.createdAt,
            updatedAt: converted.updated_at || converted.updatedAt,
            created_at: converted.created_at || converted.createdAt,
            updated_at: converted.updated_at || converted.updatedAt,
          } as User;
          if (!results.find((u) => u.id === user.id)) {
            results.push(user);
          }
        } else {
          console.log("[searchUsers] ⚠️ UID 不存在:", originalQuery);
        }
      } catch (error) {
        console.error("[searchUsers] ❌ UID 搜尋失敗:", error);
      }
    }

    // 3. 如果結果不足，載入部分使用者進行客戶端模糊搜尋（針對 displayName 和 email）
    // 使用分頁查詢分批載入，避免一次性載入所有用戶
    // 注意：如果已經通過 UID 直接查詢找到結果，可以跳過客戶端搜尋（除非需要更多結果）
    const shouldSkipClientSearch = results.length > 0 && originalQuery.length >= 10 && 
                                   results.some(u => u.id === originalQuery);
    
    if (results.length < 10 && !shouldSkipClientSearch) {
      console.log("[searchUsers] 結果不足，開始客戶端模糊搜尋，目前結果數:", results.length);
      console.log("[searchUsers] 搜尋配置:", { maxUsersToLoad, batchSize, stopOnFirstMatch });
      
      try {
        let lastDoc: QueryDocumentSnapshot | null = null;
        let totalLoaded = 0;
        let clientSideMatches = 0;
        let hasMore = true;
        let useOrderBy = true;
        
        // 先嘗試使用 orderBy，如果失敗則不使用排序
        while (totalLoaded < maxUsersToLoad && results.length < 10 && hasMore) {
          try {
            let batchQuery;
            
            if (useOrderBy) {
              // 嘗試使用 orderBy
              try {
                if (lastDoc) {
                  batchQuery = query(
                    usersCollection,
                    orderBy("created_at", "desc"),
                    startAfter(lastDoc),
                    limit(batchSize)
                  );
                } else {
                  batchQuery = query(
                    usersCollection,
                    orderBy("created_at", "desc"),
                    limit(batchSize)
                  );
                }
              } catch (orderByError) {
                // 如果 orderBy 失敗（可能是因為沒有索引），改用不使用排序
                console.warn("[searchUsers] orderBy 失敗，改用不使用排序:", orderByError);
                useOrderBy = false;
                continue; // 重新嘗試不使用排序
              }
            } else {
              // 不使用排序
              if (lastDoc) {
                batchQuery = query(
                  usersCollection,
                  startAfter(lastDoc),
                  limit(batchSize)
                );
              } else {
                batchQuery = query(usersCollection, limit(batchSize));
              }
            }
            
            const batchSnapshot: QuerySnapshot = await getDocs(batchQuery);
            const batchSizeActual = batchSnapshot.size;
            
            if (batchSizeActual === 0) {
              hasMore = false;
              console.log("[searchUsers] 沒有更多用戶可載入");
              break;
            }
            
            totalLoaded += batchSizeActual;
            console.log(`[searchUsers] 載入批次: ${batchSizeActual} 個使用者，總計: ${totalLoaded}`);
            
            // 處理這批用戶
            batchSnapshot.forEach((doc: any) => {
              const data = doc.data();
              const converted = convertTimestampToDate(data);
              const user = {
                id: doc.id,
                ...converted,
                createdAt: converted.created_at || converted.createdAt,
                updatedAt: converted.updated_at || converted.updatedAt,
                created_at: converted.created_at || converted.createdAt,
                updated_at: converted.updated_at || converted.updatedAt,
              } as User;

              // 檢查是否已在結果中
              if (results.find((u) => u.id === user.id)) {
                return;
              }

              let matched = false;

              // 模糊匹配 displayName
              const displayName = (user.displayName || "").toLowerCase();
              if (displayName.includes(queryStr)) {
                results.push(user);
                clientSideMatches++;
                matched = true;
              }

              // 模糊匹配 email（如果查詢字串包含 @ 或看起來像 email）
              if (!matched && (queryStr.includes("@") || queryStr.length > 3)) {
                const email = (user.email || "").toLowerCase();
                if (email && email.includes(queryStr)) {
                  results.push(user);
                  clientSideMatches++;
                  matched = true;
                }
              }
              
              // 如果 email 欄位不存在，嘗試通過 UID 匹配（如果查詢字串看起來像 UID）
              if (!matched) {
                if (originalQuery.length >= 10) {
                  // 大小寫不敏感匹配
                  if (user.id.toLowerCase().includes(originalQuery.toLowerCase()) || 
                      user.id === originalQuery) {
                    console.log("[searchUsers] ✅ 客戶端 UID 匹配:", user.id, "匹配", originalQuery);
                    results.push(user);
                    clientSideMatches++;
                    matched = true;
                  }
                }
              }
              
              // 如果找到匹配且設置了 stopOnFirstMatch，可以提前停止
              if (matched && stopOnFirstMatch) {
                hasMore = false;
              }
            });
            
            // 更新 lastDoc 以便下一批查詢
            if (batchSizeActual > 0) {
              lastDoc = batchSnapshot.docs[batchSnapshot.docs.length - 1];
            }
            
            // 如果這批載入的數量少於 batchSize，表示沒有更多數據了
            if (batchSizeActual < batchSize) {
              hasMore = false;
            }
            
            // 如果找到足夠的結果，可以提前停止
            if (results.length >= 10) {
              console.log("[searchUsers] 已找到足夠的結果，提前停止載入");
              hasMore = false;
            }
            
          } catch (batchError) {
            console.error("[searchUsers] 批次載入失敗:", batchError);
            // 如果使用 orderBy 失敗，嘗試不使用排序
            if (useOrderBy) {
              console.warn("[searchUsers] 嘗試不使用排序");
              useOrderBy = false;
              lastDoc = null; // 重置，從頭開始
              totalLoaded = 0;
            } else {
              // 如果兩種方式都失敗，停止載入
              hasMore = false;
              break;
            }
          }
        }
        
        console.log("[searchUsers] 客戶端搜尋完成:", {
          總載入: totalLoaded,
          找到匹配: clientSideMatches,
          總結果: results.length
        });
        
      } catch (error) {
        console.error("[searchUsers] 客戶端搜尋失敗:", error);
      }
    }

    // 限制結果數量
    const finalResults = results.slice(0, 50);
    console.log("[searchUsers] 搜尋完成，返回", finalResults.length, "個結果");
    return finalResults;
  } catch (error) {
    console.error("Error searching users:", error);
    throw error;
  }
}

/**
 * 獲取單個使用者詳細資訊
 */
export async function getUser(userId: string): Promise<User | null> {
  if (!userId || userId.trim().length === 0) {
    throw new Error("User ID is required");
  }
  try {
    const userDoc = await getDoc(doc(db, "users", userId));
    if (!userDoc.exists()) {
      return null;
    }

    const data = userDoc.data();
    const converted = convertTimestampToDate(data);
    // 確保字段名稱映射：created_at -> createdAt, updated_at -> updatedAt, lastLoginAt -> last_login
    return {
      id: userDoc.id,
      ...converted,
      createdAt: converted.created_at || converted.createdAt,
      updatedAt: converted.updated_at || converted.updatedAt,
      created_at: converted.created_at || converted.createdAt,
      updated_at: converted.updated_at || converted.updatedAt,
      last_login: converted.last_login || converted.lastLoginAt,
    } as User;
  } catch (error) {
    console.error(`Error getting user ${userId}:`, error);
    throw error;
  }
}

/**
 * 獲取使用者的交易記錄
 */
export async function getUserReceipts(
  userId: string,
  limitCount: number = 100
): Promise<Receipt[]> {
  if (!userId || userId.trim().length === 0) {
    throw new Error("User ID is required");
  }
  if (limitCount < 1 || limitCount > 1000) {
    throw new Error("Limit count must be between 1 and 1000");
  }
  try {
    const receiptsCollection = collection(db, "users", userId, "receipts");
    const q = query(
      receiptsCollection,
      orderBy("created_at", "desc"),
      limit(limitCount)
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...convertTimestampToDate(doc.data()),
    })) as Receipt[];
  } catch (error) {
    console.error(`Error getting receipts for user ${userId}:`, error);
    throw error;
  }
}

/**
 * 獲取使用者的購買失敗記錄
 */
export async function getUserFailedPurchases(
  userId: string,
  limitCount: number = 50
): Promise<FailedPurchase[]> {
  if (!userId || userId.trim().length === 0) {
    throw new Error("User ID is required");
  }
  if (limitCount < 1 || limitCount > 500) {
    throw new Error("Limit count must be between 1 and 500");
  }
  try {
    const failedPurchasesCollection = collection(db, "failed_purchases");
    const q = query(
      failedPurchasesCollection,
      where("user_id", "==", userId),
      orderBy("timestamp", "desc"),
      limit(limitCount)
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...convertTimestampToDate(doc.data()),
    })) as FailedPurchase[];
  } catch (error) {
    console.error(
      `Error getting failed purchases for user ${userId}:`,
      error
    );
    // 如果查詢失敗（可能是因為沒有索引），返回空陣列
    if (error instanceof Error && error.message.includes("index")) {
      console.warn("Index may not exist for failed_purchases query");
      return [];
    }
    throw error;
  }
}

/**
 * 獲取使用者的觀看歷史
 */
export async function getUserWatchHistory(
  userId: string,
  limitCount: number = 50
): Promise<WatchHistory[]> {
  if (!userId || userId.trim().length === 0) {
    throw new Error("User ID is required");
  }
  if (limitCount < 1 || limitCount > 500) {
    throw new Error("Limit count must be between 1 and 500");
  }
  const watchHistoryCollection = collection(
    db,
    "users",
    userId,
    "watch_history"
  );
  try {
    const q = query(
      watchHistoryCollection,
      orderBy("last_watched_at", "desc"),
      limit(limitCount)
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...convertTimestampToDate(doc.data()),
    })) as WatchHistory[];
  } catch (error) {
    console.error(`Error getting watch history for user ${userId}:`, error);
    // 如果查詢失敗（可能是因為沒有索引），嘗試不排序
    if (error instanceof Error && error.message.includes("index")) {
      console.warn("Index may not exist, trying without orderBy");
      try {
        const q2 = query(watchHistoryCollection, limit(limitCount));
        const querySnapshot2 = await getDocs(q2);
        return querySnapshot2.docs.map((doc) => ({
          id: doc.id,
          ...convertTimestampToDate(doc.data()),
        })) as WatchHistory[];
      } catch (retryError) {
        console.error("Retry failed:", retryError);
        return [];
      }
    }
    throw error;
  }
}

/**
 * 獲取使用者的收藏列表
 */
export async function getUserFavorites(
  userId: string,
  limitCount: number = 100
): Promise<Favorite[]> {
  if (!userId || userId.trim().length === 0) {
    throw new Error("User ID is required");
  }
  if (limitCount < 1 || limitCount > 1000) {
    throw new Error("Limit count must be between 1 and 1000");
  }
  const favoritesCollection = collection(db, "users", userId, "favorites");
  try {
    const q = query(
      favoritesCollection,
      orderBy("added_at", "desc"),
      limit(limitCount)
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...convertTimestampToDate(doc.data()),
    })) as Favorite[];
  } catch (error) {
    console.error(`Error getting favorites for user ${userId}:`, error);
    // 如果查詢失敗（可能是因為沒有索引），嘗試不排序
    if (error instanceof Error && error.message.includes("index")) {
      console.warn("Index may not exist, trying without orderBy");
      try {
        const q2 = query(favoritesCollection, limit(limitCount));
        const querySnapshot2 = await getDocs(q2);
        return querySnapshot2.docs.map((doc) => ({
          id: doc.id,
          ...convertTimestampToDate(doc.data()),
        })) as Favorite[];
      } catch (retryError) {
        console.error("Retry failed:", retryError);
        return [];
      }
    }
    throw error;
  }
}

/**
 * 獲取使用者全劇購買記錄
 */
export async function getUserFullDramaPurchases(
  userId: string,
  limitCount: number = 100
): Promise<
  Array<{
    id: string;
    product_id?: string;
    drama_id: string;
    drama_title?: string;
    drama_cover_url?: string;
    total_episodes?: number;
    purchased_at: Date;
    transaction_id?: string;
    platform?: string;
  }>
> {
  console.log(`[getUserFullDramaPurchases] 開始查詢，userId: ${userId}, limitCount: ${limitCount}`);
  
  if (!userId || userId.trim().length === 0) {
    console.error("[getUserFullDramaPurchases] User ID is required");
    throw new Error("User ID is required");
  }
  if (limitCount < 1 || limitCount > 1000) {
    console.error(`[getUserFullDramaPurchases] Invalid limitCount: ${limitCount}`);
    throw new Error("Limit count must be between 1 and 1000");
  }
  
  const fullDramaPurchasesCollection = collection(
    db,
    "users",
    userId,
    "full_drama_purchases"
  );
  console.log(`[getUserFullDramaPurchases] Collection path: users/${userId}/full_drama_purchases`);
  
  try {
    console.log(`[getUserFullDramaPurchases] 嘗試使用 orderBy 查詢...`);
    const q = query(
      fullDramaPurchasesCollection,
      orderBy("purchased_at", "desc"),
      limit(limitCount)
    );
    const querySnapshot = await getDocs(q);
    console.log(`[getUserFullDramaPurchases] 查詢成功，找到 ${querySnapshot.docs.length} 個文檔`);

    const results = querySnapshot.docs.map((doc) => {
      const data = doc.data();
      const converted = convertTimestampToDate(data);
      console.log(`[getUserFullDramaPurchases] 文檔 ${doc.id}:`, {
        product_id: data.product_id,
        drama_id: data.drama_id,
        drama_title: data.drama_title,
        purchased_at: converted.purchased_at || converted.purchasedAt,
        transaction_id: data.transaction_id,
        platform: data.platform,
      });
      return {
        id: doc.id,
        product_id: data.product_id || doc.id,
        drama_id: data.drama_id,
        drama_title: data.drama_title,
        drama_cover_url: data.drama_cover_url,
        total_episodes: data.total_episodes,
        purchased_at: (converted.purchased_at || converted.purchasedAt) as Date,
        transaction_id: data.transaction_id,
        platform: data.platform,
      };
    });
    
    console.log(`[getUserFullDramaPurchases] 返回 ${results.length} 個結果`);
    return results;
  } catch (error) {
    console.error(`[getUserFullDramaPurchases] 查詢失敗:`, error);
    console.error(`[getUserFullDramaPurchases] 錯誤詳情:`, {
      name: error instanceof Error ? error.name : "Unknown",
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
    });
    
    // 如果查詢失敗（可能是因為沒有索引或權限問題），嘗試不排序
    if (error instanceof Error) {
      const errorMessage = error.message.toLowerCase();
      console.log(`[getUserFullDramaPurchases] 錯誤訊息包含: index=${errorMessage.includes("index")}, permission=${errorMessage.includes("permission")}, insufficient=${errorMessage.includes("insufficient")}`);
      
      if (errorMessage.includes("index") || 
          errorMessage.includes("permission") || 
          errorMessage.includes("insufficient")) {
        console.warn("[getUserFullDramaPurchases] 嘗試不使用 orderBy 查詢...");
        try {
          const q2 = query(fullDramaPurchasesCollection, limit(limitCount));
          const querySnapshot2 = await getDocs(q2);
          console.log(`[getUserFullDramaPurchases] 不使用 orderBy 查詢成功，找到 ${querySnapshot2.docs.length} 個文檔`);
          
          const results = querySnapshot2.docs.map((doc) => {
            const data = doc.data();
            const converted = convertTimestampToDate(data);
            return {
              id: doc.id,
              product_id: data.product_id || doc.id,
              drama_id: data.drama_id,
              drama_title: data.drama_title,
              drama_cover_url: data.drama_cover_url,
              total_episodes: data.total_episodes,
              purchased_at: (converted.purchased_at || converted.purchasedAt) as Date,
              transaction_id: data.transaction_id,
              platform: data.platform,
            };
          });
          
          console.log(`[getUserFullDramaPurchases] 返回 ${results.length} 個結果（不使用排序）`);
          return results;
        } catch (retryError) {
          console.error("[getUserFullDramaPurchases] 重試失敗:", retryError);
          console.error("[getUserFullDramaPurchases] 重試錯誤詳情:", {
            name: retryError instanceof Error ? retryError.name : "Unknown",
            message: retryError instanceof Error ? retryError.message : String(retryError),
            stack: retryError instanceof Error ? retryError.stack : undefined,
          });
          
          // 如果是權限問題，返回空陣列而不是拋出錯誤
          if (retryError instanceof Error) {
            const retryErrorMessage = retryError.message.toLowerCase();
            if (retryErrorMessage.includes("permission") || 
                retryErrorMessage.includes("insufficient")) {
              console.warn("[getUserFullDramaPurchases] 權限被拒絕，返回空陣列");
              return [];
            }
          }
          throw retryError;
        }
      }
    }
    throw error;
  }
}

/**
 * 獲取使用者已解鎖的影片（保留此函數以向後兼容）
 */
export async function getUserUnlockedVideos(
  userId: string,
  limitCount: number = 100
): Promise<
  Array<{
    id: string;
    video_id: string;
    drama_id: string;
    unlocked_at: Date;
    cost: number;
  }>
> {
  console.log(`[getUserUnlockedVideos] 開始查詢，userId: ${userId}, limitCount: ${limitCount}`);
  
  if (!userId || userId.trim().length === 0) {
    console.error("[getUserUnlockedVideos] User ID is required");
    throw new Error("User ID is required");
  }
  if (limitCount < 1 || limitCount > 1000) {
    console.error(`[getUserUnlockedVideos] Invalid limitCount: ${limitCount}`);
    throw new Error("Limit count must be between 1 and 1000");
  }
  
  const unlockedVideosCollection = collection(
    db,
    "users",
    userId,
    "unlocked_videos"
  );
  console.log(`[getUserUnlockedVideos] Collection path: users/${userId}/unlocked_videos`);
  
  try {
    console.log(`[getUserUnlockedVideos] 嘗試使用 orderBy 查詢...`);
    const q = query(
      unlockedVideosCollection,
      orderBy("unlocked_at", "desc"),
      limit(limitCount)
    );
    const querySnapshot = await getDocs(q);
    console.log(`[getUserUnlockedVideos] 查詢成功，找到 ${querySnapshot.docs.length} 個文檔`);

    const results = querySnapshot.docs.map((doc) => {
      const data = doc.data();
      console.log(`[getUserUnlockedVideos] 文檔 ${doc.id}:`, {
        video_id: data.video_id,
        drama_id: data.drama_id,
        unlocked_at: data.unlocked_at,
        cost: data.cost,
      });
      return {
        id: doc.id,
        ...convertTimestampToDate(data),
      };
    }) as Array<{
      id: string;
      video_id: string;
      drama_id: string;
      unlocked_at: Date;
      cost: number;
    }>;
    
    console.log(`[getUserUnlockedVideos] 返回 ${results.length} 個結果`);
    return results;
  } catch (error) {
    console.error(`[getUserUnlockedVideos] 查詢失敗:`, error);
    console.error(`[getUserUnlockedVideos] 錯誤詳情:`, {
      name: error instanceof Error ? error.name : "Unknown",
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
    });
    
    // 如果查詢失敗（可能是因為沒有索引或權限問題），嘗試不排序
    if (error instanceof Error) {
      const errorMessage = error.message.toLowerCase();
      console.log(`[getUserUnlockedVideos] 錯誤訊息包含: index=${errorMessage.includes("index")}, permission=${errorMessage.includes("permission")}, insufficient=${errorMessage.includes("insufficient")}`);
      
      if (errorMessage.includes("index") || 
          errorMessage.includes("permission") || 
          errorMessage.includes("insufficient")) {
        console.warn("[getUserUnlockedVideos] 嘗試不使用 orderBy 查詢...");
        try {
          const q2 = query(unlockedVideosCollection, limit(limitCount));
          const querySnapshot2 = await getDocs(q2);
          console.log(`[getUserUnlockedVideos] 不使用 orderBy 查詢成功，找到 ${querySnapshot2.docs.length} 個文檔`);
          
          const results = querySnapshot2.docs.map((doc) => ({
            id: doc.id,
            ...convertTimestampToDate(doc.data()),
          })) as Array<{
            id: string;
            video_id: string;
            drama_id: string;
            unlocked_at: Date;
            cost: number;
          }>;
          
          console.log(`[getUserUnlockedVideos] 返回 ${results.length} 個結果（不使用排序）`);
          return results;
        } catch (retryError) {
          console.error("[getUserUnlockedVideos] 重試失敗:", retryError);
          console.error("[getUserUnlockedVideos] 重試錯誤詳情:", {
            name: retryError instanceof Error ? retryError.name : "Unknown",
            message: retryError instanceof Error ? retryError.message : String(retryError),
            stack: retryError instanceof Error ? retryError.stack : undefined,
          });
          
          // 如果是權限問題，返回空陣列而不是拋出錯誤
          if (retryError instanceof Error) {
            const retryErrorMessage = retryError.message.toLowerCase();
            if (retryErrorMessage.includes("permission") || 
                retryErrorMessage.includes("insufficient")) {
              console.warn("[getUserUnlockedVideos] 權限被拒絕，返回空陣列");
              return [];
            }
          }
          throw retryError;
        }
      }
    }
    throw error;
  }
}


// ============================================================
// CMS 後台手動發放免費金幣（Admin only）
// ============================================================

/**
 * 後台發放免費金幣給指定用戶。
 * @param userId 目標用戶 UID
 * @param amount 發放數量（正整數，上限 100000）
 * @param reason 發放原因（必填，會寫入 receipt 與 transaction）
 */
export async function grantFreeCoin(
  userId: string,
  amount: number,
  reason: string
): Promise<{
  transaction_id: string;
  new_paid: number;
  new_free: number;
  new_total: number;
}> {
  const auth = getAuth(app);
  const user = auth.currentUser;
  if (!user) throw new Error("用戶未登入");
  const token = await user.getIdToken();

  const projectId =
    app.options.projectId ||
    import.meta.env.VITE_FIREBASE_PROJECT_ID ||
    "onreel-sandbox";
  const url = `https://asia-east1-${projectId}.cloudfunctions.net/grantFreeCoin`;

  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ user_id: userId, amount, reason }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${res.status}`);
  }

  return res.json();
}

